<template>
  <div id="app" class="app-container">
    <Menubar :model="items" class="mb-4 print-hide">
      <template #start>
        <div class="flex align-items-center cursor-pointer" @click="router.push('/')">
          <span class="text-xl font-bold mr-2">ScrapBoss</span>
        </div>
      </template>
      <template #end>
        <div class="flex align-items-center gap-2">
          <Button
            icon="pi pi-check-square"
            :class="['p-button-text', taskButtonClass]"
            @click="router.push('/crm/tasks')"
            :badge="taskBadgeCount"
            :badgeClass="taskBadgeClass"
            v-tooltip.top="taskTooltipContent"
          />
          <ExtrasMenu />
        </div>
      </template>
    </Menubar>
    <div class="layout-content">
      <router-view />
    </div>
    <div class="layout-footer text-center print-hide text-500 p-4 mb-4">
      © 2025 ScrapBoss. All rights reserved.
    </div>
    <Toast position="top-right" />
    <ConfirmDialog />
    <CallLogger />
    <StatusBar />
    <DebugDialog />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import Menubar from 'primevue/menubar'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import Tooltip from 'primevue/tooltip'
import CallLogger from '@/components/crm/CallLogger.vue'
import { useRouter } from 'vue-router'
import ExtrasMenu from '@/components/common/ExtrasMenu.vue'
import StatusBar from '@/components/common/StatusBar.vue'
import DebugDialog from '@/components/debug/DebugDialog.vue'
import { useTaskStore } from '@/stores/taskStore'
import { useDebugStore } from '@/stores/debugStore'
import type { TaskStatus } from '@/types/Task'

const router = useRouter()
const taskStore = useTaskStore()
const debugStore = useDebugStore()

// Register tooltip directive
const vTooltip = Tooltip

// Task badge logic
const taskBadgeCount = ref('0')
const overdueCount = ref(0)
const dueTodayCount = ref(0)
const dueThisWeekCount = ref(0)

// Watch for task changes and update the badge count
watch(
  () => taskStore.items,
  (tasks) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    // Calculate end of week (next Sunday)
    const endOfWeek = new Date(today)
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()))

    // Filter out completed tasks first
    const activeTasks = tasks.filter((task) => task.status !== ('done' as TaskStatus))

    const overdueTasks = activeTasks.filter((task) => task.dueAt && new Date(task.dueAt) < today)

    const dueTodayTasks = activeTasks.filter(
      (task) => task.dueAt && new Date(task.dueAt) >= today && new Date(task.dueAt) < tomorrow,
    )

    const dueThisWeekTasks = activeTasks.filter(
      (task) => task.dueAt && new Date(task.dueAt) >= tomorrow && new Date(task.dueAt) <= endOfWeek,
    )

    overdueCount.value = overdueTasks.length
    dueTodayCount.value = dueTodayTasks.length
    dueThisWeekCount.value = dueThisWeekTasks.length
    const totalCount = overdueCount.value + dueTodayCount.value + dueThisWeekCount.value
    taskBadgeCount.value = totalCount.toString()
  },
  { immediate: true },
)

// Reset debug store on route navigation
watch(
  () => router.currentRoute.value.path,
  () => {
    debugStore.clearOperations()
  },
)

const taskBadgeClass = computed(() => {
  if (overdueCount.value > 0) return 'p-badge-danger'
  if (dueTodayCount.value > 0) return 'p-badge-warning'
  if (dueThisWeekCount.value > 0) return 'p-badge-info'
  return 'p-badge-info'
})

const taskTooltipContent = computed(() => {
  return `Tasks
${overdueCount.value} overdue
${dueTodayCount.value} due today
${dueThisWeekCount.value} due this week`
})

const taskButtonClass = computed(() => {
  if (overdueCount.value > 0) return 'text-red-500'
  if (dueTodayCount.value > 0) return 'text-orange-500'
  return 'text-blue-500'
})

const items = ref([
  {
    label: 'New',
    icon: 'pi pi-plus',
    items: [
      {
        label: 'Purchase',
        icon: 'pi pi-shopping-cart',
        command: () => router.push('/purchases/new'),
      },
      {
        label: 'Sale',
        icon: 'pi pi-dollar',
        command: () => router.push('/sales/new'),
      },
      {
        label: 'Email',
        icon: 'pi pi-envelope',
        command: () => router.push('/crm/emailer'),
      },
    ],
  },
  {
    label: 'Transactions',
    icon: 'pi pi-sync',
    items: [
      {
        label: 'Purchases',
        icon: 'pi pi-shopping-cart',
        command: () => router.push('/transaction?type=purchase'),
      },
      {
        label: 'Sales',
        icon: 'pi pi-dollar',
        command: () => router.push('/transaction?type=sale'),
      },
      {
        label: 'All Transactions',
        icon: 'pi pi-sync',
        command: () => router.push('/transaction'),
      },
      {
        label: 'Commodities',
        icon: 'pi pi-tags',
        command: () => router.push('/commodity'),
      },
    ],
  },
  {
    label: 'CRM',
    icon: 'pi pi-address-book',
    items: [
      {
        label: 'People',
        icon: 'pi pi-user',
        command: () => router.push('/crm/people'),
      },
      {
        label: 'Organizations',
        icon: 'pi pi-building',
        command: () => router.push('/crm/organizations'),
      },
      {
        label: 'Email',
        icon: 'pi pi-envelope',
        items: [
          {
            label: 'Send Email',
            icon: 'pi pi-send',
            command: () => router.push('/crm/emailer'),
          },
          {
            label: 'Email History',
            icon: 'pi pi-history',
            command: () => router.push('/crm/emails'),
          },
          {
            label: 'Email Templates',
            icon: 'pi pi-objects-column',
            command: () => router.push('/crm/email-templates'),
          },
          {
            label: 'Email Lists',
            icon: 'pi pi-list',
            command: () => router.push('/crm/email-lists'),
          },
        ],
      },
      // {
      //   label: 'Activity',
      //   icon: 'pi pi-list',
      //   command: () => router.push('/crm/timeline'),
      // },
    ],
  },
  {
    label: 'Reports',
    icon: 'pi pi-chart-bar',
    items: [
      {
        label: 'Business Overview',
        icon: 'pi pi-chart-bar',
        command: () => router.push('/reports/business-overview'),
      },
      {
        label: 'All Reports',
        icon: 'pi pi-list',
        command: () => router.push('/reports'),
      },
      {
        label: 'Transaction Reports',
        icon: 'pi pi-sync',
        command: () => router.push('/transaction/reports'),
      },
    ],
  },
])
</script>

<style>
/* Global styles */
html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
}

.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
}

.layout-content {
  flex: 1;
  margin: 0 auto;
  width: 100%;
  padding: 0 0.5rem;
}

/* Only add horizontal padding on larger screens */
@media screen and (min-width: 768px) {
  .layout-content {
    padding: 0 1rem;
  }
}

.layout-footer {
  margin-top: auto;
}

/* Override PrimeVue Toast z-index to ensure it's always on top */
:deep(.p-toast) {
  z-index: 9999;
}

/* Make sure Menubar is responsive */
@media screen and (max-width: 768px) {
  .p-menubar {
    padding: 0.5rem;
  }

  .p-menubar-button {
    display: block !important;
  }
}

/* Task badge styles */
:deep(.p-badge) {
  min-width: 1.5rem;
  height: 1.5rem;
  line-height: 1.5rem;
  font-size: 0.75rem;
}

:deep(.p-badge-danger) {
  background: var(--red-500);
}

:deep(.p-badge-warning) {
  background: var(--orange-500);
}

:deep(.p-badge-info) {
  background: var(--blue-500);
}

/* Tooltip styles */
:deep(.p-tooltip) {
  max-width: 200px;
}

:deep(.p-tooltip-text) {
  padding: 0.5rem;
  font-size: 0.875rem;
}
</style>
